import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api } from '../../lib/api';

export type Role = 'ADMIN' | 'ASSISTANT' | 'SUPERVISOR' | 'TEACHER' | 'STUDENT' | 'PARENT';

export interface AuthUser {
  id: string;
  email: string;
  role: Role;
  firstName: string;
  lastName: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  register: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    referralCode?: string;
    turnstileToken?: string;
  }) => Promise<{ email: string }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Les tokens vivent désormais dans des cookies httpOnly (le JS ne peut plus les lire) :
  // on détermine la session en cours via l'API, qui reçoit le cookie automatiquement.
  async function loadCurrentUser(): Promise<AuthUser | null> {
    try {
      // skipAuthRefresh : un visiteur anonyme sur une page vitrine échoue ici en 401 par
      // design (pas de session) — inutile de tenter un refresh ni de rediriger vers /login.
      const { data } = await api.get('/profile', { skipAuthRefresh: true });
      const loadedUser = { id: data.id, email: data.email, role: data.role, firstName: data.firstName, lastName: data.lastName };
      setUser(loadedUser);
      return loadedUser;
    } catch {
      setUser(null);
      return null;
    }
  }

  useEffect(() => {
    loadCurrentUser().finally(() => setIsLoading(false));
  }, []);

  async function login(email: string, password: string): Promise<AuthUser> {
    await api.post('/auth/login', { email, password });
    const loadedUser = await loadCurrentUser();
    if (!loadedUser) {
      throw new Error('Impossible de récupérer la session après connexion.');
    }
    return loadedUser;
  }

  async function register(payload: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    referralCode?: string;
    turnstileToken?: string;
  }): Promise<{ email: string }> {
    const { data } = await api.post('/auth/register', payload);
    return data;
  }

  async function logout() {
    await api.post('/auth/logout').catch(() => undefined);
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{ user, isLoading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
